/** React Query hooks: the query cache invalidates itself in place of the Power Apps manual collections. */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import { nextSalesOffset } from './salesPaging'
import type { ProductMoveResult, ProductUsage, ProductUsagePerson } from './productUsage'
import type {
  BankAccount,
  Client,
  ClientSummary,
  IssuerIdentity,
  PriceListItem,
  SalesDocument,
  SalesDocumentPage,
  User,
} from './types'

export const keys = {
  me: ['me'] as const,
  sales: (filters?: Record<string, unknown>) => ['sales', filters ?? {}] as const,
  salesDocument: (id: string) => ['sales', 'document', id] as const,
  clients: (search?: string) => ['clients', search ?? ''] as const,
  organizationSites: ['organization-sites'] as const,
  priceList: (search?: string) => ['price-list', search ?? ''] as const,
  issuerIdentities: ['issuer-identities'] as const,
  bankAccounts: ['bank-accounts'] as const,
}

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => (await api.get<User>('/api/auth/me')).data,
    retry: false,
    // Not Infinity: a change to a user's tier/permissions must eventually reach nav/route gating without a hard reload.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })
}

export function useSalesDocuments(filters: Record<string, string | undefined> = {}) {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== ''),
  )
  return useQuery({
    queryKey: keys.sales(params),
    queryFn: async () =>
      (await api.get<SalesDocumentPage>('/api/sales', { params })).data,
    // Keeps the previous page while typing or paging: otherwise isLoading flips on every key and SalesList collapses to one placeholder cell, losing scroll position.
    placeholderData: keepPreviousData,
  })
}

/** Every page of the sales list loaded so far; the next page is requested by `offset`, and a changed filter or sort starts over. */
export function useSalesDocumentsInfinite(filters: Record<string, string | undefined> = {}) {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== ''),
  )
  return useInfiniteQuery({
    queryKey: ['sales', 'infinite', params] as const,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      (await api.get<SalesDocumentPage>('/api/sales', { params: { ...params, offset: pageParam } })).data,
    getNextPageParam: (last, all) =>
      nextSalesOffset(all.reduce((count, page) => count + page.rows.length, 0), last.total),
    placeholderData: keepPreviousData,
  })
}

export function useSalesDocument(id: string | undefined) {
  return useQuery({
    queryKey: keys.salesDocument(id ?? ''),
    queryFn: async () => (await api.get<SalesDocument>(`/api/sales/${id}`)).data,
    enabled: Boolean(id),
  })
}

export function useSaveSalesDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: string; payload: unknown }) => {
      const response = id
        ? await api.put<SalesDocument>(`/api/sales/${id}`, payload)
        : await api.post<SalesDocument>('/api/sales', payload)
      return response.data
    },
    onSuccess: (document) => {
      queryClient.invalidateQueries({ queryKey: ['sales'] })
      queryClient.setQueryData(keys.salesDocument(document.id), document)
    },
  })
}

// `numbering_fallback` is kept out of the shared `SalesDocument` type: only this endpoint sets it, so every other read would return `""` anyway.
type IssuedDocument = SalesDocument & {
  numbering_fallback: string
}

// One selectable SITE, not one address row: `organization_address` holds a row per (site, language) and the backend collapses them, so `address_id` may be any row of the site.
export interface OrganizationSite {
  address_id: string
  kind: string
  label: string
  address: string
  is_primary: boolean
}

/** The sites the issue dialog offers as the document's address. Served by the SALES router because `/api/organization/addresses` is gated behind `admin`.
 *  `staleTime: Infinity`: branches change rarely, and a stale option is harmless since the id is re-resolved at issue time and a deleted one is rejected with a 409. */
export function useOrganizationSites() {
  return useQuery({
    queryKey: keys.organizationSites,
    queryFn: async () =>
      (await api.get<OrganizationSite[]>('/api/sales/organization-sites')).data,
    staleTime: Infinity,
  })
}

export interface SignAsOptions {
  allowed: boolean
  candidates: { person_id: string; name: string }[]
}

/** Whether the caller may print an accountant's signature on a sales PDF, and whose. */
export function useSignAsOptions() {
  return useQuery({
    queryKey: ['sales', 'sign-as-options'] as const,
    queryFn: async () => (await api.get<SignAsOptions>('/api/sales/sign-as-options')).data,
    staleTime: 60_000,
  })
}

export function useIssueDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      sendEmail,
      organizationAddressId,
      language,
      signaturePersonId,
    }: {
      id: string
      sendEmail: boolean
      // Whose signature the stored PDF carries; omitted -> the issuer's own.
      signaturePersonId?: string | null
      // The document's own language. Omitted -> "mn", what every document was rendered in before the choice existed.
      language?: 'mn' | 'en' | 'ru'
      // Omitted -> the backend auto-resolves the primary site. Sent as `null` rather than left off so the intent is explicit on the wire.
      organizationAddressId?: string | null
    }) =>
      (
        await api.post<IssuedDocument>(`/api/sales/${id}/issue`, {
          send_email: sendEmail,
          organization_address_id: organizationAddressId ?? null,
          language: language ?? 'mn',
          signature_person_id: signaturePersonId ?? null,
        })
      ).data,
    onSuccess: (document) => {
      // The document is now immutable, so every cached copy is stale.
      queryClient.invalidateQueries({ queryKey: ['sales'] })
      queryClient.setQueryData(keys.salesDocument(document.id), document)
    },
  })
}

export function useReviseDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<SalesDocument>(`/api/sales/${id}/revise`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales'] }),
  })
}

export function useConvertToInvoice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<SalesDocument>(`/api/sales/${id}/convert`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales'] }),
  })
}

export function useVoidDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      (await api.post<SalesDocument>(`/api/sales/${id}/void`, { reason })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales'] }),
  })
}

export function useUpdatePaymentStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      paymentStatus,
      paidDate,
    }: {
      id: string
      paymentStatus: string
      paidDate: string | null
    }) =>
      (
        await api.patch<SalesDocument>(`/api/sales/${id}/payment`, {
          payment_status: paymentStatus,
          paid_date: paidDate,
        })
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales'] }),
  })
}

// Records one entry against `payment_history`, the only route that can put a document into Partial (derived from the sum). Unlike the PATCH, the server 409s on an amount over the balance instead of clamping, so callers must surface the rejection.
export function useRecordPayment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      amount,
      paidDate,
      note,
    }: {
      id: string
      amount: string
      paidDate: string
      note?: string
    }) =>
      (
        await api.post<SalesDocument>(`/api/sales/${id}/payments`, {
          amount,
          paid_date: paidDate,
          note: note ?? '',
        })
      ).data,
    onSuccess: (document) => {
      queryClient.invalidateQueries({ queryKey: ['sales'] })
      queryClient.setQueryData(keys.salesDocument(document.id), document)
    },
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/sales/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales'] }),
  })
}

/** Under the `sales` key so every sales write that invalidates the list refreshes the balances too. */
export function useClientSummaries(clientId?: string) {
  return useQuery({
    queryKey: ['sales', 'client-summary', clientId ?? ''] as const,
    queryFn: async () =>
      (
        await api.get<ClientSummary[]>('/api/sales/client-summary', {
          params: clientId ? { client_id: clientId } : {},
        })
      ).data,
  })
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ['clients', 'one', id ?? ''] as const,
    queryFn: async () => (await api.get<Client>(`/api/clients/${id}`)).data,
    enabled: !!id,
  })
}

export function useClients(search?: string) {
  return useQuery({
    queryKey: keys.clients(search),
    // `limit: 500` is the route's own `le=`; without it the default of 100 applies and the page and picker silently lose the 101st client.
    queryFn: async () =>
      (
        await api.get<Client[]>('/api/clients', {
          params: { limit: 500, ...(search ? { search } : {}) },
        })
      ).data,
  })
}

// The server refuses (409) while any document references the client.
export function useDeleteClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/clients/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  })
}

export function useSaveClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: string; payload: unknown }) => {
      const response = id
        ? await api.put<Client>(`/api/clients/${id}`, payload)
        : await api.post<Client>('/api/clients', payload)
      return response.data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  })
}

// Both are org-scoped by the backend (RLS plus an explicit `org_id` filter), so there is no per-client parameter: one fetch serves every row of `Clients.tsx`.
export function useIssuerIdentities() {
  return useQuery({
    queryKey: keys.issuerIdentities,
    queryFn: async () => (await api.get<IssuerIdentity[]>('/api/clients/issuer-identities')).data,
  })
}

export function useBankAccounts() {
  return useQuery({
    queryKey: keys.bankAccounts,
    queryFn: async () => (await api.get<BankAccount[]>('/api/clients/bank-accounts')).data,
  })
}

export function usePriceList(search?: string) {
  return useQuery({
    queryKey: keys.priceList(search),
    // `limit: 500` is the route's own `le=`; without it the default of 200 applies and, sorted by group/product, cuts off every SEEQUENT row.
    queryFn: async () =>
      (
        await api.get<PriceListItem[]>('/api/price-list', {
          params: { limit: 500, ...(search ? { search } : {}) },
        })
      ).data,
  })
}

// A product's uses, moving them, deleting an unused one: `hr.edit`, like the product PATCH. Typed in `lib/productUsage.ts`.

export function useProductUsage(id: string | null) {
  return useQuery({
    queryKey: ['hr', 'products', id, 'usage'],
    enabled: id != null,
    queryFn: async () => (await api.get<ProductUsage>(`/api/hr/products/${id}/usage`)).data,
  })
}

export function useProductUsagePeople(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['hr', 'products', id, 'usage', 'people'],
    enabled: id != null && enabled,
    queryFn: async () =>
      (await api.get<ProductUsagePerson[]>(`/api/hr/products/${id}/usage/people`)).data,
  })
}

/** `confirm: false` is the dry run; only a confirmed move invalidates. */
export function useMoveProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, targetId, confirm }: { id: string; targetId: string; confirm: boolean }) =>
      (
        await api.post<ProductMoveResult>(`/api/hr/products/${id}/move`, {
          target_id: targetId,
          confirm,
        })
      ).data,
    onSuccess: (result) => {
      if (!result.applied) return
      queryClient.invalidateQueries({ queryKey: ['hr', 'products'] })
      queryClient.invalidateQueries({ queryKey: ['time'] })
      queryClient.invalidateQueries({ queryKey: ['payroll'] })
      queryClient.invalidateQueries({ queryKey: ['roster'] })
    },
  })
}

export function useDeleteProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/hr/products/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'products'] })
      queryClient.invalidateQueries({ queryKey: ['time', 'products'] })
      queryClient.invalidateQueries({ queryKey: ['roster', 'projects'] })
    },
  })
}
